import api from '../../../lib/api'

export interface ScheduleConfig {
  task_key: string
  display_name: string
  description: string
  minute: string
  hour: string
  day_of_week: string
  day_of_month: string
  month_of_year: string
  is_enabled: boolean
  allowed_roles: string[]
  updated_at: string | null
  updated_by_id: number | null
}

export interface ScheduleConfigUpdate {
  minute?: string
  hour?: string
  day_of_week?: string
  day_of_month?: string
  month_of_year?: string
  is_enabled?: boolean
}

export const schedulerService = {
  async listSchedules(): Promise<ScheduleConfig[]> {
    const { data } = await api.get('/admin/schedules')
    return data
  },

  async updateSchedule(taskKey: string, payload: ScheduleConfigUpdate): Promise<ScheduleConfig> {
    const { data } = await api.patch(`/admin/schedules/${taskKey}`, payload)
    return data
  },
}

/**
 * Convert stored cron fields back to a human-readable summary.
 * Not a full cron parser — covers the patterns actually used in seed data.
 */
export function describeCron(cfg: Pick<ScheduleConfig, 'minute' | 'hour' | 'day_of_week'>): string {
  const { minute, hour, day_of_week: dow } = cfg

  // Every N minutes
  if (minute.startsWith('*/') && hour === '*' && dow === '*') {
    const n = minute.slice(2)
    return `Every ${n} min`
  }
  // Every N hours (at :00)
  if (minute === '0' && hour.startsWith('*/') && dow === '*') {
    const n = hour.slice(2)
    return `Every ${n}h`
  }
  // Daily at specific hour
  if (minute === '0' && /^\d+$/.test(hour) && dow === '*') {
    const h = parseInt(hour, 10)
    const ampm = h === 0 ? 'midnight' : h < 12 ? `${h}am` : h === 12 ? 'noon' : `${h - 12}pm`
    return `Daily ${ampm}`
  }
  // Weekly on specific day
  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  if (minute === '0' && /^\d+$/.test(hour) && /^\d+$/.test(dow)) {
    const h = parseInt(hour, 10)
    const dayName = DAY_NAMES[parseInt(dow, 10)] ?? `day ${dow}`
    const ampm = h === 0 ? 'midnight' : `${h}:00`
    return `${dayName} ${ampm}`
  }
  // Fallback: show raw cron
  return `${minute} ${hour} * * ${dow}`
}
